import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Save, Settings, FileText } from 'lucide-react';
import { AJUSTEMENT_TYPES, AJUSTEMENT_CIBLES} from './constants';

export default function ModalsAjustement({
  dialogOpen, setDialogOpen,
  editMode, setEditMode,
  formData, setFormData: setFormData,
  handleInputChange,
  handleSubmit,
  importDialogOpen, setImportDialogOpen,
  importData,
  confirmImport,
  fileInputRef: _fileInputRef,
  handleFileImport: _handleFileImport,
  previewDialogOpen, setPreviewDialogOpen,
  previewParams,
  customParamDialogOpen, setCustomParamDialogOpen,
  editingCustomParam,
  customParamForm, setCustomParamForm,
  handleSaveCustomParam,
  toggleApplicableA,
  restrictionDialogOpen, setRestrictionDialogOpen,
  editingRestriction,
  restrictionForm, setRestrictionForm,
  handleSaveRestriction,
  territoiresDisponibles,
  ajustementDialogOpen, setAjustementDialogOpen,
  editingAjustement,
  ajustementForm, setAjustementForm,
  handleCreateAjustement,
  handleUpdateAjustement,
  handleEdit}) {
  return (
    <>
        <Dialog open={ajustementDialogOpen} onOpenChange={setAjustementDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-purple-600" />
                {editingAjustement ? 'Modifier l\'ajustement' : 'Nouvel ajustement de montant'}
              </DialogTitle>
              <DialogDescription>
                Personnalisez les montants affichés dans le simulateur et les documents générés
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Nom et Description */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ajust-nom">Nom de l&apos;ajustement *</Label>
                  <Input
                    id="ajust-nom"
                    value={ajustementForm.nom}
                    onChange={(e) => setAjustementForm({...ajustementForm, nom: e.target.value})}
                    placeholder="Ex: Tarif préférentiel PME"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ajust-priorite">Priorité</Label>
                  <Input
                    id="ajust-priorite"
                    type="number"
                    value={ajustementForm.priorite}
                    onChange={(e) => setAjustementForm({...ajustementForm, priorite: parseInt(e.target.value) || 0})}
                    placeholder="0"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="ajust-description">Description</Label>
                <Input
                  id="ajust-description"
                  value={ajustementForm.description}
                  onChange={(e) => setAjustementForm({...ajustementForm, description: e.target.value})}
                  placeholder="Description de l&apos;ajustement..."
                />
              </div>
              
              {/* Type d'ajustement */}
              <div className="space-y-3">
                <Label>Type d&apos;ajustement</Label>
                <div className="grid grid-cols-3 gap-3">
                  {AJUSTEMENT_TYPES.map((type) => (
                    <div
                      key={type.value}
                      onClick={() => setAjustementForm({...ajustementForm, type_ajustement: type.value})}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        ajustementForm.type_ajustement === type.value 
                          ? 'border-purple-600 bg-purple-50' 
                          : 'border-slate-200 hover:border-purple-300'
                      }`}
                    >
                      <div className="text-2xl mb-2">{type.icon}</div>
                      <div className="font-semibold">{type.label}</div>
                      <div className="text-xs text-slate-500">{type.description}</div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Valeurs de remplacement */}
              {ajustementForm.type_ajustement === 'remplacement' && (
                <div className="border border-purple-200 rounded-xl p-4 bg-purple-50">
                  <h4 className="font-semibold mb-4 flex items-center gap-2">
                    <span className="text-xl">🔄</span>
                    Valeurs de remplacement
                  </h4>
                  <p className="text-sm text-slate-600 mb-4">
                    Ces valeurs remplaceront les montants calculés automatiquement
                  </p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="val-ht" className="flex items-center gap-2">
                        <span className="w-6 h-6 bg-blue-600 rounded text-white text-xs flex items-center justify-center">MB</span>
                        Montant de base (€)
                      </Label>
                      <Input
                        id="val-ht"
                        type="number"
                        step="0.01"
                        value={ajustementForm.valeur_remplacement_ht}
                        onChange={(e) => setAjustementForm({...ajustementForm, valeur_remplacement_ht: e.target.value})}
                        placeholder="Laisser vide pour ne pas modifier"
                        className="font-mono"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="val-ttc" className="flex items-center gap-2">
                        <span className="w-6 h-6 bg-green-600 rounded text-white text-xs flex items-center justify-center">GL</span>
                        Montant Global (€)
                      </Label>
                      <Input
                        id="val-ttc"
                        type="number"
                        step="0.01"
                        value={ajustementForm.valeur_remplacement_ttc}
                        onChange={(e) => setAjustementForm({...ajustementForm, valeur_remplacement_ttc: e.target.value})}
                        placeholder="Laisser vide pour ne pas modifier"
                        className="font-mono"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="val-ca" className="flex items-center gap-2">
                        <span className="w-6 h-6 bg-amber-600 rounded text-white text-xs flex items-center justify-center">CA</span>
                        Chiffre d&apos;affaires (€)
                      </Label>
                      <Input
                        id="val-ca"
                        type="number"
                        step="0.01"
                        value={ajustementForm.valeur_remplacement_ca}
                        onChange={(e) => setAjustementForm({...ajustementForm, valeur_remplacement_ca: e.target.value})}
                        placeholder="Laisser vide pour ne pas modifier"
                        className="font-mono"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Valeur pourcentage ou fixe */}
              {ajustementForm.type_ajustement !== 'remplacement' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ajust-cible">Cible de l&apos;ajustement</Label>
                    <Select 
                      value={ajustementForm.cible} 
                      onValueChange={(v) => setAjustementForm({...ajustementForm, cible: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AJUSTEMENT_CIBLES.map((c) => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ajust-valeur">
                      Valeur {ajustementForm.type_ajustement === 'pourcentage' ? '(%)' : '(€)'}
                    </Label>
                    <Input
                      id="ajust-valeur"
                      type="number"
                      step={ajustementForm.type_ajustement === 'pourcentage' ? '0.1' : '0.01'}
                      value={ajustementForm.valeur}
                      onChange={(e) => setAjustementForm({...ajustementForm, valeur: parseFloat(e.target.value) || 0})}
                      placeholder={ajustementForm.type_ajustement === 'pourcentage' ? 'Ex: 10 pour +10%' : 'Ex: 50'}
                      className="font-mono"
                    />
                  </div>
                </div>
              )}
              
              {/* Options d'affichage sur documents */}
              <div className="border border-slate-200 rounded-xl p-4">
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  Options pour les documents
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Afficher sur les documents générés</Label>
                      <p className="text-sm text-slate-500">Les montants personnalisés apparaîtront sur les attestations et factures</p>
                    </div>
                    <Switch
                      checked={ajustementForm.afficher_sur_documents}
                      onCheckedChange={(checked) => setAjustementForm({...ajustementForm, afficher_sur_documents: checked})}
                    />
                  </div>
                  
                  {ajustementForm.afficher_sur_documents && (
                    <div className="space-y-2">
                      <Label htmlFor="libelle-doc">Libellé sur les documents (optionnel)</Label>
                      <Input
                        id="libelle-doc"
                        value={ajustementForm.libelle_document}
                        onChange={(e) => setAjustementForm({...ajustementForm, libelle_document: e.target.value})}
                        placeholder="Ex: Tarif préférentiel appliqué"
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Statut actif */}
              <div className="flex items-center justify-between p-4 border border-slate-200 rounded-xl">
                <div>
                  <Label>Ajustement actif</Label>
                  <p className="text-sm text-slate-500">Désactivez pour suspendre temporairement l&apos;ajustement</p>
                </div>
                <Switch
                  checked={ajustementForm.is_active}
                  onCheckedChange={(checked) => setAjustementForm({...ajustementForm, is_active: checked})}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setAjustementDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={editingAjustement ? handleUpdateAjustement : handleCreateAjustement}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {editingAjustement ? 'Mettre à jour' : 'Créer l\'ajustement'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </>
  );
}
